'use client';

import clsx from 'clsx';
import { MarketingHeroIllustration } from '@/assets/icons';
import { PAGE_HERO_BAND_CLASS } from '@/constants/pageSections';
import { Heading1 } from '@/components/section/headings';
import AppLink from '@/components/i18n/AppLink';
import { Span } from '@/components/ui';
import { scrollToHelpSupportForm } from '@/lib/help/scrollToHelpTarget';
import { HELP_SUPPORT_FORM_ID } from '@/constants/helpCenter';

export type MarketingHubHeroProps = {
  headingId: string;
  kicker: string;
  title: string;
  intro: string;
  consultLabel: string;
  secondaryLabel: string;
  secondaryHref: string;
  /** Marketing path for theme-aware SVG hero art (e.g. `services`, `ai-agents/conversational-ai`). */
  illustrationPath?: string;
  /** `split` = copy + illustration; `centered` = text only */
  variant?: 'split' | 'centered';
  /** Override default primary CTA (`/consultation`). */
  primaryHref?: string;
  primaryLabel?: string;
  className?: string;
};

const MarketingHubHero: React.FC<MarketingHubHeroProps> = ({
  headingId,
  kicker,
  title,
  intro,
  consultLabel,
  secondaryLabel,
  secondaryHref,
  illustrationPath,
  variant = 'split',
  primaryHref = '/consultation',
  primaryLabel,
  className,
}) => {
  const useSplitLayout = variant === 'split' && Boolean(illustrationPath);
  const primaryText = primaryLabel ?? consultLabel;
  const scrollTargetId = primaryHref?.startsWith('#') ? primaryHref.slice(1) : null;

  const primaryCta =
    scrollTargetId ? (
      <button
        type="button"
        className="btn btn-primary shrink-0"
        onClick={() => {
          if (scrollTargetId === HELP_SUPPORT_FORM_ID) {
            const narrow = window.matchMedia('(max-width: 1023px)').matches;
            if (narrow) {
              window.location.hash = HELP_SUPPORT_FORM_ID;
              window.dispatchEvent(new HashChangeEvent('hashchange'));
              return;
            }
            scrollToHelpSupportForm();
            return;
          }
          document.getElementById(scrollTargetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      >
        {primaryText}
      </button>
    ) : (
      <AppLink href={primaryHref} btn="primary" className="shrink-0">
        {primaryText}
      </AppLink>
    );

  return (
    <section className={clsx(PAGE_HERO_BAND_CLASS, className)} aria-labelledby={headingId}>
      <div className={clsx(useSplitLayout ? 'page-hero-split' : 'mx-auto w-full min-w-0 max-w-3xl')}>
        <div className="min-w-0 text-center md:text-start">
          <Span className="text-xs font-semibold uppercase tracking-[0.2em] text-prose-muted">{kicker}</Span>
          <div className="mt-3" id={headingId}>
            <Heading1
              title={title}
              subtitle={intro}
              titleClassName="text-balance text-prose"
              subtitleClassName="mt-4 max-w-2xl text-prose-muted leading-relaxed mx-auto md:mx-0"
            />
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3 sm:mt-8 md:justify-start">
            {primaryCta}
            <AppLink
              href={secondaryHref}
              className="text-sm font-medium text-prose-muted underline-offset-4 hover:text-prose hover:underline"
            >
              {secondaryLabel}
            </AppLink>
          </div>
        </div>
        {useSplitLayout && illustrationPath ? (
          <div className="page-hero-media">
            <div className="page-hero-media-frame page-hero-media-frame--illustration">
              <MarketingHeroIllustration path={illustrationPath} />
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default MarketingHubHero;
